import { FC, ReactNode } from 'react'
import { TrendsChartSelectDropdownIcon } from 'ui/components/svg'
import { ListItemText, MenuItem, OutlinedInput, SelectProps, Select } from '@mui/material'
import { CategoryMenuProps, TrendsChartDescriptionFormControl } from './trendsChartDescription.styles'
import theme from 'theme'

interface Props extends SelectProps {
  options: string[]
  value: any
  handleChange: any
  minWidth?: string
  noborder?: string
  labeltext?: string
  disabled: boolean
  handleCustomClick?: () => void
  anchorEl: any
  setAnchorEl: any
  closeCustomPicker: any
  customTimePeriodValue: string
  hideSelected?: boolean
  pickerOpen?: boolean
  inlineLabel?: string
}

const TrendsChartCustomSelectField: FC<Props> = ({
  options,
  labeltext,
  handleChange,
  placeholder,
  noborder,
  minWidth,
  value,
  disabled,
  handleCustomClick,
  anchorEl,
  setAnchorEl,
  closeCustomPicker,
  customTimePeriodValue,
  hideSelected,
  pickerOpen,
  inlineLabel,
  ...rest
}) => {
  const handleClick = (event: any) => {
    if (disabled) return
    setAnchorEl(event.currentTarget)
  }

  // Opt-in: prefix the rendered value with a muted label shown inside the box (e.g. "Time Period:").
  const withLabel = (node: ReactNode) =>
    inlineLabel ? (
      <>
        <span className="inline-filter-label">{inlineLabel}</span> {node}
      </>
    ) : (
      node
    )

  return (
    <TrendsChartDescriptionFormControl
      sx={{ minWidth }}
      autowidth={inlineLabel ? 'true' : undefined}
      isselected={
        hideSelected
          ? 'false'
          : placeholder !== 'Sort' && (value.length > 1 || (value.length === 1 && value[0] !== ''))
          ? 'true'
          : 'false'
      }
      is_opened={anchorEl ? 'true' : 'false'}
      is_disabled={disabled ? 'true' : 'false'}
      noborder={noborder}
    >
      <Select
        labelId="library-select-label"
        id="library-select"
        displayEmpty
        MenuProps={{
          ...CategoryMenuProps,
          PaperProps: {
            ...CategoryMenuProps.PaperProps,
            style: { maxHeight: 'unset', display: anchorEl ? 'block' : 'none' }
          },
          anchorEl,
          open: Boolean(anchorEl),
          onClose: () => setAnchorEl(null)
        }}
        sx={{
          minWidth,
          '& .MuiSelect-icon': { transform: anchorEl ? 'rotate(180deg)' : 'rotate(0deg)' }
        }}
        IconComponent={(props) => <TrendsChartSelectDropdownIcon {...props} />}
        renderValue={(selected) => {
          if (customTimePeriodValue) return withLabel(customTimePeriodValue)
          if (Array.isArray(selected)) {
            const pruned = selected.filter((s: string) => s !== '')
            if (pruned.length === 0) {
              return withLabel(placeholder)
            }
            if (pruned.length === 1) {
              return withLabel(pruned[0])
            }

            return withLabel(`${pruned[0]} +${pruned.length - 1}`)
          }

          if (labeltext) {
            return withLabel(typeof noborder !== 'undefined' ? `${labeltext} ${selected}` : selected)
          }
          return withLabel(typeof noborder !== 'undefined' ? `Sort by: ${selected}` : selected)
        }}
        inputProps={{ 'aria-label': 'Without label' }}
        {...rest}
        value={value}
        onChange={handleChange}
        input={<OutlinedInput onMouseDown={handleClick} />}
      >
        {options.map((option) => (
          <MenuItem
            key={option}
            value={option}
            onClick={() => {
              if (!rest.multiple) {
                setAnchorEl(null)
                closeCustomPicker()
              }
            }}
            disableRipple
          >
            <ListItemText primary={option} />
            {value === option && (
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M2.625 7.4375L6.125 10.9375L11.375 3.0625"
                  stroke={theme.colors.textPrimary}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </MenuItem>
        ))}

        {/* Custom Picker Option */}
        <MenuItem
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            background: pickerOpen
              ? `${theme.colors.surfaceContainerBackground}`
              : customTimePeriodValue
              ? `${theme.colors.surfaceContainerBackground}`
              : '',

            '.MuiListItemText-root': {
              width: 'fit-content',
              maxWidth: '60px',

              '.MuiTypography-root': {
                width: 'fit-content',
                color: pickerOpen || customTimePeriodValue ? `${theme.colors.textPrimary} !important` : ''
              }
            },

            svg: {
              path: {
                stroke: pickerOpen || customTimePeriodValue ? `${theme.colors.textPrimary} !important` : ''
              }
            },

            '&:hover': {
              svg: {
                path: {
                  stroke: `${theme.colors.textPrimary}`
                }
              }
            }
          }}
          onClick={handleCustomClick}
        >
          <ListItemText primary="Custom" />
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M5.49969 3L10.4997 8L5.49969 13"
              stroke={theme.baseColors.greyscale[80]}
              strokeWidth="1.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </MenuItem>
      </Select>
    </TrendsChartDescriptionFormControl>
  )
}

export default TrendsChartCustomSelectField
